// =======================================================================
// Excel Import — parsing + validation ONLY. No database writes happen
// here; this module produces a validated, categorized row list that the
// UI presents for review before any Supabase write is made elsewhere.
// =======================================================================
import ExcelJS from 'exceljs'

const VALID_UNITS_HINT = null // no fixed unit whitelist exists in the schema; presence is what's required

export async function parseImportFile(file) {
  const wb = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await wb.xlsx.load(buffer)

  const ws = wb.worksheets.find((s) => s.rowCount > 1) // first non-empty sheet
  if (!ws) throw new Error('The uploaded file has no readable sheet.')

  // Find the header row by looking for a row containing "Description" —
  // tolerant of the template's title/info rows above it.
  let headerRowIdx = null
  let colMap = {}
  for (let i = 1; i <= Math.min(ws.rowCount, 10); i++) {
    const row = ws.getRow(i)
    const values = row.values.map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v))
    const descIdx = values.findIndex((v) => v === 'description')
    if (descIdx > -1) {
      headerRowIdx = i
      values.forEach((v, idx) => {
        if (v === 'item no' || v === 'item no.' || v === 'item_no') colMap.itemNo = idx
        if (v === 'description') colMap.description = idx
        if (v === 'unit') colMap.unit = idx
        if (v === 'quantity') colMap.quantity = idx
      })
      break
    }
  }
  if (headerRowIdx === null || colMap.description == null || colMap.unit == null || colMap.quantity == null) {
    throw new Error('Could not find the expected columns (Description, Unit, Quantity) in this file. Please use the provided template.')
  }

  const rows = []
  for (let i = headerRowIdx + 1; i <= ws.rowCount; i++) {
    const row = ws.getRow(i)
    if (row.values.length === 0 || row.values.every((v) => v == null || v === '')) continue // skip fully blank rows

    const itemNoRaw = colMap.itemNo != null ? row.getCell(colMap.itemNo).value : null
    const itemNo = itemNoRaw != null && String(itemNoRaw).trim() !== '' ? String(itemNoRaw).trim() : null
    const description = row.getCell(colMap.description).value
    const unit = row.getCell(colMap.unit).value
    const quantityRaw = row.getCell(colMap.quantity).value

    rows.push({
      rowNumber: i,
      itemNo,
      description: description != null ? String(description).trim() : '',
      unit: unit != null ? String(unit).trim() : '',
      quantity: quantityRaw,
    })
  }
  return rows
}

// -----------------------------------------------------------------------
// Validate parsed rows against basic rules AND against the existing
// BOQ items already in the target revision (fetched by the caller).
// Returns each row annotated with { status, issues, action }.
//
// status ∈ 'valid' | 'warning' | 'error' | 'ambiguous'
// action ∈ 'create' | 'update' | null (null when status is error/ambiguous)
// -----------------------------------------------------------------------
export function validateImportRows(rows, existingItemsByNo) {
  // Detect duplicate item_no WITHIN the uploaded file. NULL item_no is
  // never considered a duplicate of another NULL — only non-null,
  // non-empty values are checked for duplication.
  const nonNullCounts = {}
  for (const r of rows) {
    if (r.itemNo) nonNullCounts[r.itemNo] = (nonNullCounts[r.itemNo] || 0) + 1
  }
  const ambiguousItemNos = new Set(Object.keys(nonNullCounts).filter((k) => nonNullCounts[k] > 1))

  return rows.map((r) => {
    const issues = []
    let status = 'valid'
    let action = 'create'

    if (r.itemNo && ambiguousItemNos.has(r.itemNo)) {
      status = 'ambiguous'
      action = null
      issues.push(`Item No "${r.itemNo}" appears more than once in this file.`)
      return { ...r, status, action, issues }
    }

    if (!r.description) { issues.push('Description is required.'); status = 'error' }
    if (!r.unit) { issues.push('Unit is required.'); status = 'error' }

    const qtyNum = Number(r.quantity)
    if (r.quantity == null || r.quantity === '' || Number.isNaN(qtyNum)) {
      issues.push('Quantity is missing or not a valid number.')
      status = 'error'
    } else if (qtyNum < 0) {
      issues.push('Quantity cannot be negative.')
      status = 'error'
    }

    if (status === 'error') { action = null; return { ...r, status, action, issues } }

    const existing = r.itemNo ? existingItemsByNo[r.itemNo] : null
    if (existing) {
      action = 'update'
      issues.push('An item with this Item No already exists in this revision.')
      if (existing.hasPricing) {
        status = 'warning'
        issues.push('This BOQ item already contains pricing data. Updating its description, unit, or quantity may affect the estimate.')
      }
    }

    return { ...r, status, action, quantity: qtyNum, issues }
  })
}
